import React, { useEffect, useState } from 'react';
import { CalendarDays, CheckSquare, Lock } from 'lucide-react';
import UserAvatar from './ui/UserAvatar';
import TaskLabelChips from './TaskLabelChips';
import { resolveLabels } from '../extensions/bundled/task-manager/boardLabels';
import TaskPriorityBars from './boards/TaskPriorityBars';
import * as boardsStore from '../extensions/bundled/task-manager/boardsStore';
import TaskQuickViewModal from './TaskQuickViewModal';

/**
 * A shared board task, rendered in the chat transcript.
 *
 * The message carries ids and nothing else (see utils/taskChatShare.js), so
 * everything visible here is resolved from the reader's OWN synced copy of the
 * board. That is the whole design: two people looking at the same message see
 * the title if they are on the team and "no access" if they are not, without
 * the server having to decide anything and without the title ever being
 * written to a plaintext column.
 *
 * It also means the card stays honest over time. A renamed task shows its new
 * title; a deleted one says so; someone removed from the team stops being able
 * to read a card they could read yesterday.
 */
export default function TaskShareCard({ metadata }) {
  const meta = metadata || {};
  // 'loading' until the local lookup settles — never render "no access"
  // optimistically, or every card would flash a permission error on mount.
  const [state, setState] = useState({ status: 'loading', task: null, board: null });

  useEffect(() => {
    let cancelled = false;
    if (!meta.board_id || !meta.task_id || !meta.team_id) {
      setState({ status: 'invalid', task: null, board: null });
      return undefined;
    }

    (async () => {
      try {
        const scope = boardsStore.teamScopeId(meta.team_id);
        const board = await boardsStore.readBoard(meta.board_id, scope);
        const task = (board?.tasks || []).find((t) => String(t.id) === String(meta.task_id));
        if (cancelled) return;
        // No board is the ordinary not-on-this-team case; a board without the
        // task means it was deleted since the message was sent.
        if (!board) setState({ status: 'no-access', task: null, board: null });
        else if (!task) setState({ status: 'missing', task: null, board });
        else setState({ status: 'ok', task, board });
      } catch {
        if (!cancelled) setState({ status: 'no-access', task: null, board: null });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [meta.board_id, meta.task_id, meta.team_id]);

  // Opens the TASK, in place, over the conversation — not the board it lives
  // on. Following a link to a card should show you the card; navigating to a
  // whole other view to read one task loses your place in the chat you were
  // reading, and getting back is a manual trip.
  const [quickView, setQuickView] = useState(false);
  const open = () => {
    if (state.status !== 'ok') return;
    setQuickView(true);
  };

  if (state.status === 'loading') {
    return (
      <div className="chat-share-card chat-task-share">
        <span className="chat-task-share-sub">Loading task…</span>
      </div>
    );
  }

  if (state.status !== 'ok') {
    const text =
      state.status === 'missing'
        ? 'This task has been deleted.'
        : state.status === 'invalid'
          ? 'This task link is incomplete.'
          : // Says what is true and what to do about it. "Failed to load" would
            // send people looking for a bug that is actually a permission.
            'You do not have access to this task. Ask to be added to the team.';
    return (
      <div className="chat-share-card chat-task-share chat-task-share--locked">
        <Lock size={14} aria-hidden />
        <span className="chat-task-share-sub">{text}</span>
      </div>
    );
  }

  const { task, board } = state;
  const labels = resolveLabels(task, board);
  const due = task.dueDate ? new Date(task.dueDate) : null;

  return (
    <>
      <button type="button" className="chat-share-card chat-task-share" onClick={open}>
      <span className="chat-task-share-head">
        <TaskPriorityBars priority={task.priority || 'medium'} />
        <span
          className={`chat-task-share-title${task.done ? ' chat-task-share-title--done' : ''}`}
        >
          {task.title || 'Untitled task'}
        </span>
        {task.done && <span className="chat-task-share-done">Done</span>}
      </span>

      {/* The card earns its size by carrying what a board card carries — who
          owns it, what it is, when it is due. A title and a board name was a
          link wearing a border. */}
      <span className="chat-task-share-meta">
        <span className="chat-task-share-board">
          <CheckSquare size={12} aria-hidden />
          {board?.name || 'Board'}
        </span>
        {due && !Number.isNaN(due.getTime()) && (
          <span className="chat-task-share-due">
            <CalendarDays size={12} aria-hidden />
            {due.toLocaleDateString()}
          </span>
        )}
        {task.assigneeId && (
          <UserAvatar
            className="chat-task-share-avatar"
            userId={task.assigneeId}
            name="Assignee"
            size={18}
          />
        )}
      </span>

      {labels.length > 0 && <TaskLabelChips labels={labels} max={3} />}
      </button>
      {quickView && (
        <TaskQuickViewModal
          boardId={meta.board_id}
          taskId={meta.task_id}
          teamId={meta.team_id}
          onClose={() => setQuickView(false)}
        />
      )}
    </>
  );
}
